/*
 * Copyright (C) 2019 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { Component, EventEmitter, HostListener, Input, OnInit, Output } from '@angular/core';
import { LanguageCodeHelper } from '@app/app/shared/helpers/language-code-helper';
import {
  DiagnosticFormattedQuestion,
  DiagnosticQuestionOptionResponse,
  DiagnosticQuestionOptionWithSelection,
  GroupQuestionSelectedOption,
  LanguageValue,
  Organization,
  QuestionComposition,
} from '../../models';

@Component({
  selector: 'ptl-diagnostics-view-group-question-type',
  templateUrl: './group-question-type.component.html',
  styleUrls: ['./group-question-type.component.scss'],
})
export class DiagnosticsViewGroupQuestionTypeComponent implements OnInit {
  /** Receives the question to answer */
  @Input() question: DiagnosticFormattedQuestion;
  @Input() answeredQuestions;
  @Input() disabled: boolean;
  @Input() isFeedbackFor: boolean;
  @Input() currentLanguage: string;
  @Input() organization: Organization;

  formattedQuestions: DiagnosticFormattedQuestion[] = [];
  optionsTitle: string[] = [];
  isTablet: boolean;

  /** Emits the answer when selected. */
  @Output() selectedAnswer = new EventEmitter<{
    uid: string;
    answers: GroupQuestionSelectedOption[];
  }>();

  instruction: string;

  ngOnInit(): void {
    this.checkScreenWidth();
    if (this.question) {
      this.instruction =
        LanguageCodeHelper.getDataByUserLanguageCode(this.question.instructions, this.organization, this.currentLanguage).value ?? '';
      this.formattedQuestions = this.getFormattedGroupQuestion(this.question.questionComposition);
      this.optionsTitle = this.getOptionsTitle(this.question.questionComposition.options);

      const selectedQuestion = this.answeredQuestions[this.question.uid];
      if (selectedQuestion) {
        for (const item of selectedQuestion) {
          const questionIndex = this.formattedQuestions.findIndex((q) => q.uid === item.questionUid);
          for (const option of item.selectedOptions) {
            const optionIndex = this.question.questionComposition.options.findIndex((o) => o.uid === option.optionUid);
            this.selectAnswer(questionIndex, optionIndex, true);
          }
        }
      }
    }
  }

  getTranslatedValue(values: LanguageValue[]) {
    return LanguageCodeHelper.getDataByUserLanguageCode(values, this.organization, this.currentLanguage).value;
  }

  @HostListener('window:resize', [])
  onResize() {
    this.checkScreenWidth();
  }

  selectAnswer(questionIndex: number, optionIndex: number, override = false): void {
    if (this.disabled && !override) {
      return;
    }
    if (this.formattedQuestions[questionIndex]) {
      this.formattedQuestions[questionIndex].options?.map((option, index) => {
        option.selected = optionIndex === index;
        return option;
      });
    }

    this.triggerUserSelectedAnswers();
  }

  private checkScreenWidth() {
    this.isTablet = window.innerWidth <= 900;
  }

  private getFormattedGroupQuestion(composition: QuestionComposition): DiagnosticFormattedQuestion[] {
    const questions: DiagnosticFormattedQuestion[] = [];
    if (!composition) {
      return questions;
    }
    composition.questions.map((q) => {
      const instruction = LanguageCodeHelper.getDataByUserLanguageCode(q.instructions, this.organization, this.currentLanguage);

      questions.push({
        choices: [],
        title: instruction.value,
        instruction: instruction,
        value: undefined,
        ...q,
        options: this.getFormattedGroupQuestionsOptions(q.options),
      });
    });
    return questions;
  }

  private getFormattedGroupQuestionsOptions(options: DiagnosticQuestionOptionWithSelection[]): DiagnosticQuestionOptionWithSelection[] {
    const formattedOptions: DiagnosticQuestionOptionWithSelection[] = [];
    for (const option of options) {
      formattedOptions.push({
        ...option,
        selected: false,
      });
    }
    return formattedOptions;
  }

  private getOptionsTitle(options: DiagnosticQuestionOptionResponse[]): string[] {
    const titles: string[] = [];
    for (const option of options) {
      const instruction = LanguageCodeHelper.getDataByUserLanguageCode(option.values, this.organization, this.currentLanguage);
      titles.push(instruction.value);
    }
    return titles;
  }

  private triggerUserSelectedAnswers(): void {
    const questionUid = this.question.uid;

    const answers: GroupQuestionSelectedOption[] = this.formattedQuestions.reduce((acc, question) => {
      const selectedOption = question.options.find((option) => option.selected);
      if (selectedOption) {
        acc.push({ question: question, option: selectedOption });
      }
      return acc;
    }, []);

    const allAnswered = answers.length === this.formattedQuestions.length;

    if (allAnswered) {
      this.selectedAnswer.emit({ uid: questionUid, answers });
    }
  }
}
